/**
 * Slice 37 — Owner Unit tab: Quick check (unlimited). Copy from locked canvas.
 */
import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import {
  useCreateWalkQuickCheck,
  useDeleteWalkQuickCheck,
  useUpdateWalkQuickCheck,
  useWalkQuickChecks,
} from "@/hooks/useWalkQuickChecks";

interface UnitQuickCheckSectionProps {
  propertyId: string;
  className?: string;
}

export function UnitQuickCheckSection({ propertyId, className }: UnitQuickCheckSectionProps) {
  const { data: rows = [], isLoading } = useWalkQuickChecks(propertyId);
  const createRow = useCreateWalkQuickCheck();
  const updateRow = useUpdateWalkQuickCheck();
  const deleteRow = useDeleteWalkQuickCheck();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const saveNew = async () => {
    const label = draft.trim();
    if (!label) return;
    setBusy(true);
    try {
      await createRow.mutateAsync({ propertyId, label });
      setDraft("");
      setAdding(false);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : "Couldn’t save",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id: string) => {
    const label = editDraft.trim();
    if (!label) {
      setEditingId(null);
      return;
    }
    setBusy(true);
    try {
      await updateRow.mutateAsync({ id, propertyId, label });
      setEditingId(null);
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : "Couldn’t save",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await deleteRow.mutateAsync({ id, propertyId });
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : "Couldn’t remove",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className={cn("flex justify-center py-10 text-[#64748b]", className)}>
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-4",
        className,
      )}
      data-tour="tour-unit-quick-check"
    >
      <div>
        <div className="text-[18px] font-bold tracking-[-0.3px] text-[#0f1720]">Quick check</div>
        <div className="mt-0.5 text-[13.5px] font-medium text-[#8494a2]">
          Cleaners tap OK or Problem on Walk.
        </div>
      </div>

      {rows.length === 0 && !adding ? (
        <div className="flex flex-col items-center gap-1.5 rounded-[14px] border-[1.5px] border-dashed border-[#d7e0e6] bg-[#f9fbfc] px-[18px] py-[26px] text-center">
          <div className="text-[16px] font-bold text-[#475569]">No quick checks</div>
          <div className="text-[13.5px] font-medium text-[#8494a2]">
            Cleaners only flag problems.
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "flex flex-col gap-2",
            rows.length > 5 && "max-h-[280px] overflow-y-auto overscroll-contain pr-0.5",
          )}
        >
          {rows.map((row) => {
            const editing = editingId === row.id;
            return (
              <div
                key={row.id}
                className="flex items-center gap-2 rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#fbfdfd] px-3 py-2.5"
              >
                {editing ? (
                  <input
                    autoFocus
                    value={editDraft}
                    maxLength={40}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onBlur={() => void saveEdit(row.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                      if (e.key === "Escape") {
                        setEditingId(null);
                      }
                    }}
                    className="h-10 min-w-0 flex-1 rounded-lg border-[1.5px] border-[#24a89a] bg-white px-3 text-[15px] font-bold text-[#0f1720] outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(row.id);
                      setEditDraft(row.label);
                    }}
                    className="min-w-0 flex-1 truncate text-left text-[15px] font-bold text-[#0f1720]"
                  >
                    {row.label}
                  </button>
                )}
                <button
                  type="button"
                  aria-label="Remove"
                  disabled={busy}
                  onClick={() => void remove(row.id)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#8494a2] hover:bg-[#f1f5f8] hover:text-[#b4453a]"
                >
                  <X className="h-4 w-4" strokeWidth={2.4} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {adding ? (
        <div className="flex flex-col gap-2">
          <input
            autoFocus
            value={draft}
            maxLength={40}
            placeholder="One line"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void saveNew();
              if (e.key === "Escape") {
                setAdding(false);
                setDraft("");
              }
            }}
            className="h-12 w-full rounded-[13px] border-[1.5px] border-[#dde5ea] bg-white px-3.5 text-[15px] font-medium outline-none placeholder:text-[#a2b0b9] focus:border-[#24a89a]"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !draft.trim()}
              onClick={() => void saveNew()}
              className="flex h-11 flex-1 items-center justify-center rounded-[13px] bg-[#24a89a] text-[15px] font-bold text-white disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setDraft("");
              }}
              className="flex h-11 items-center justify-center rounded-[13px] px-4 text-[15px] font-bold text-[#64748b]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex h-[46px] items-center justify-center gap-2 rounded-[13px] border-[1.5px] border-[#cfe4e1] bg-white text-[15px] font-bold text-[#15776d]"
        >
          <Plus className="h-4 w-4" strokeWidth={2.6} />
          Add a check
        </button>
      )}
    </div>
  );
}
